import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { useOtherWindowOpen } from "../../../hooks/useOtherWindowOpen";
import { ASK_EVENT, AskEventDetail } from "../../ai/ask/askEvent";
import { AskPanel, AskRequest } from "../../ai/ask/AskPanel";
import { AskProposal, createdToast } from "../../ai/ask/proposal.utils";
import { ProposalReview } from "../../ai/ask/ProposalReview";
import { useAiStatus } from "../../ai/useAiStatus";
import SearchAll from "./SearchAll";

const HeaderSearch = () => {
  const location = useLocation();
  const boxRef = useRef<HTMLDivElement>(null);
  const { isOn: isAiOn, orgId } = useAiStatus();
  const [askRequest, setAskRequest] = useState<AskRequest | null>(null);
  // Ask ProCharted action being reviewed in its pre-filled New … window.
  const [review, setReview] = useState<AskProposal | null>(null);
  // Another window (e.g. New work) is open: the top search stays a plain
  // search and an open Ask panel is hidden, so it never covers the window
  // (QA batch 11). The review window itself is "another window" too; the panel
  // is hidden then anyway.
  const otherWindowOpen = useOtherWindowOpen();
  const askAllowed = isAiOn && (!otherWindowOpen || !!review);

  useEffect(() => {
    setAskRequest(null);
    setReview(null);
  }, [location?.pathname]);

  // "Ask ProCharted": the search box's first row (AI on only).
  const aiAsk = useMemo(
    () =>
      askAllowed
        ? {
            onAsk: (question: string) =>
              setAskRequest((current) => ({
                id: (current?.id || 0) + 1,
                question,
              })),
          }
        : undefined,
    [askAllowed],
  );
  // Questions asked elsewhere (Home "Ask about your day") open the same panel.
  useEffect(() => {
    if (!askAllowed) return undefined;
    const onAskEvent = (event: Event) => {
      const question = (event as CustomEvent<AskEventDetail>).detail?.question;
      if (question)
        setAskRequest((current) => ({ id: (current?.id || 0) + 1, question }));
    };
    window.addEventListener(ASK_EVENT, onAskEvent);
    return () => window.removeEventListener(ASK_EVENT, onAskEvent);
  }, [askAllowed]);
  const closeAsk = useCallback(() => {
    setAskRequest(null);
    setReview(null);
  }, []);
  const cancelReview = useCallback(() => setReview(null), []);
  const reviewCreated = useCallback(() => {
    // The window itself goes on to open the new work item / client.
    if (review) toast.success(createdToast(review));
    setReview(null);
    setAskRequest(null);
  }, [review]);
  // Sequence / repeat / invoice / payment open on their own page (pre-filled
  // window there): close the panel without a "created" toast.
  const reviewOpened = useCallback(() => {
    setReview(null);
    setAskRequest(null);
  }, []);

  return (
    <div ref={boxRef} className={"w-full"}>
      <SearchAll aiAsk={aiAsk} />
      {askRequest && isAiOn && (
        <AskPanel
          key={"ask"}
          request={askRequest}
          orgId={orgId}
          anchorRef={boxRef}
          onClose={closeAsk}
          onReview={setReview}
          hidden={!!review || otherWindowOpen}
        />
      )}
      {review && askRequest && isAiOn && (
        <ProposalReview
          proposal={review}
          onCancel={cancelReview}
          onCreated={reviewCreated}
          onOpened={reviewOpened}
        />
      )}
    </div>
  );
};

export default HeaderSearch;
